"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OnboardingLineasResponseSchema = exports.OnboardingLineaOptionSchema = exports.OnboardingLineasQuerySchema = exports.OnboardingCompleteSchema = exports.OnboardingHelpResponseSchema = exports.OnboardingHelpDeliveryStatusSchema = exports.OnboardingManualFallbackSchema = exports.OnboardingHelpRequestSchema = exports.OnboardingMatchResponseSchema = exports.OnboardingFormerStudentMigrationSchema = exports.OnboardingHelpContextSchema = exports.OnboardingCandidateEvidenceSchema = exports.OnboardingCandidateSchema = exports.OnboardingHelpReasonSchema = exports.OnboardingBlockCodeSchema = exports.OnboardingMatchSchema = exports.ResetPasswordPrevalidateResponseSchema = exports.ResetPasswordPrevalidateSchema = exports.ResetPasswordSchema = exports.ForgotPasswordSchema = exports.ResendVerificationSchema = exports.RegisterSchema = exports.LoginSchema = exports.UDA_INSTITUTIONAL_EMAIL_MESSAGE = exports.UDA_INSTITUTIONAL_EMAIL_DOMAIN = void 0;
exports.normalizeRegistrationEmail = normalizeRegistrationEmail;
exports.isUdaInstitutionalEmail = isUdaInstitutionalEmail;
const zod_1 = require("zod");
const investigador_schema_js_1 = require("./investigador.schema.js");
const adscripcion_js_1 = require("./adscripcion.js");
exports.UDA_INSTITUTIONAL_EMAIL_DOMAIN = '@uda.cl';
exports.UDA_INSTITUTIONAL_EMAIL_MESSAGE = 'Debes usar un correo institucional @uda.cl.';
const UDA_INSTITUTIONAL_EMAIL_PATTERN = /^[^@\s]+@uda\.cl$/;
function normalizeRegistrationEmail(correo) {
    return correo.trim().toLowerCase();
}
function isUdaInstitutionalEmail(correo) {
    return UDA_INSTITUTIONAL_EMAIL_PATTERN.test(normalizeRegistrationEmail(correo));
}
// ─── Login Schema ────────────────────────────────────────────
// Validación unificada: se usa tanto en NestJS (Pipe) como en
// el Frontend (validación client-side) desde @siiv/schemas.
// ──────────────────────────────────────────────────────────────
exports.LoginSchema = zod_1.z.object({
    correo: zod_1.z
        .string({ required_error: 'El correo es obligatorio' })
        .email('El correo no tiene un formato válido'),
    clave: zod_1.z
        .string({ required_error: 'La clave es obligatoria' })
        .min(1, 'La clave no puede estar vacía'),
    rememberMe: zod_1.z
        .boolean()
        .optional()
        .default(false),
});
// ─── Register Schema (Fase 1) ────────────────────────────────
// Primer paso del registro: correo + clave + repetir clave.
// El resto del onboarding (verificación, datos, match) viene después.
// ──────────────────────────────────────────────────────────────
exports.RegisterSchema = zod_1.z
    .object({
    correo: zod_1.z
        .string({ required_error: 'El correo es obligatorio' })
        .trim()
        .toLowerCase()
        .email('El correo no tiene un formato válido')
        .refine(isUdaInstitutionalEmail, exports.UDA_INSTITUTIONAL_EMAIL_MESSAGE),
    clave: zod_1.z
        .string({ required_error: 'La clave es obligatoria' })
        .min(8, 'La clave debe tener al menos 8 caracteres')
        .regex(/[A-Z]/, 'La clave debe incluir al menos una letra mayúscula')
        .regex(/\d/, 'La clave debe incluir al menos un número'),
    repetirClave: zod_1.z
        .string({ required_error: 'Repite la clave' })
        .min(1, 'Repite la clave'),
    rememberMe: zod_1.z.boolean().optional().default(false),
})
    .refine((data) => data.clave === data.repetirClave, {
    message: 'Las claves no coinciden',
    path: ['repetirClave'],
});
// ─── Reenviar verificación de correo ─────────────────────────
// El usuario nunca verificó (o el enlace venció) → pide un nuevo correo.
exports.ResendVerificationSchema = zod_1.z.object({
    correo: zod_1.z
        .string({ required_error: 'El correo es obligatorio' })
        .trim()
        .toLowerCase()
        .email('El correo no tiene un formato válido')
        .refine(isUdaInstitutionalEmail, exports.UDA_INSTITUTIONAL_EMAIL_MESSAGE),
});
// ─── Olvidaste tu clave — Paso 1: pedir enlace ───────────────
// Solo correo. El backend responde de forma genérica (anti-enumeración).
exports.ForgotPasswordSchema = zod_1.z.object({
    correo: zod_1.z
        .string({ required_error: 'El correo es obligatorio' })
        .trim()
        .toLowerCase()
        .email('El correo no tiene un formato válido')
        .refine(isUdaInstitutionalEmail, exports.UDA_INSTITUTIONAL_EMAIL_MESSAGE),
});
// ─── Olvidaste tu clave — Paso 2: fijar clave nueva ──────────
// Llega con el token del enlace + la clave nueva (con confirmación).
exports.ResetPasswordSchema = zod_1.z
    .object({
    token: zod_1.z
        .string({ required_error: 'Falta el token del enlace' })
        .min(1, 'Falta el token del enlace'),
    clave: zod_1.z
        .string({ required_error: 'La clave es obligatoria' })
        .min(8, 'La clave debe tener al menos 8 caracteres')
        .regex(/[A-Z]/, 'La clave debe incluir al menos una letra mayúscula')
        .regex(/\d/, 'La clave debe incluir al menos un número'),
    repetirClave: zod_1.z
        .string({ required_error: 'Repite la clave' })
        .min(1, 'Repite la clave'),
})
    .refine((data) => data.clave === data.repetirClave, {
    message: 'Las claves no coinciden',
    path: ['repetirClave'],
});
// ─── Olvidaste tu clave — Prevalidar token antes del formulario ──
// El resultado nunca debe exponer datos de cuenta ni identidad.
exports.ResetPasswordPrevalidateSchema = zod_1.z.object({
    token: zod_1.z
        .string({ required_error: 'Falta el token del enlace' })
        .min(1, 'Falta el token del enlace'),
});
exports.ResetPasswordPrevalidateResponseSchema = zod_1.z.discriminatedUnion('valid', [
    zod_1.z.object({
        valid: zod_1.z.literal(true),
    }).strict(),
    zod_1.z.object({
        valid: zod_1.z.literal(false),
        reason: zod_1.z.literal('invalid_or_expired'),
        message: zod_1.z.string().min(1),
    }).strict(),
]);
// ─── Onboarding — Paso 1: match de identidad ─────────────────
// El usuario ingresa RUT + ORCID (el correo ya lo tiene de la sesión).
// El ORCID puede venir como link o como ID; se normaliza en el backend.
exports.OnboardingMatchSchema = zod_1.z.object({
    rut: zod_1.z
        .string({ required_error: 'El RUT es obligatorio' })
        .min(1, 'El RUT es obligatorio'),
    orcid: zod_1.z
        .string({ required_error: 'El ORCID es obligatorio' })
        .min(1, 'El ORCID es obligatorio'),
});
exports.OnboardingBlockCodeSchema = zod_1.z.enum([
    'multiple_matches',
    'linked_account',
    'no_soy_identity_conflict',
    'identity_mismatch',
    'former_student_email_conflict',
]);
exports.OnboardingHelpReasonSchema = zod_1.z.union([
    exports.OnboardingBlockCodeSchema,
    zod_1.z.enum(['no_match', 'not_me', 'manual_help']),
]);
const OnboardingCandidateBaseSchema = zod_1.z.object({
    idInvestigador: zod_1.z.number().int().positive(),
    nombreCompleto: zod_1.z.string(),
    nombre1: zod_1.z.string().nullable(),
    nombre2: zod_1.z.string().nullable(),
    apellido1: zod_1.z.string().nullable(),
    apellido2: zod_1.z.string().nullable(),
    orcid: zod_1.z.string().nullable(),
    fotoPerfilUrl: zod_1.z.string().nullable(),
    publicaciones: zod_1.z.array(zod_1.z.object({ titulo: zod_1.z.string(), anio: zod_1.z.number().int().nullable() })),
    proyectos: zod_1.z.array(zod_1.z.object({ titulo: zod_1.z.string(), tipo: zod_1.z.string() })),
});
// R65: unidades vigentes del perfil existente (p. ej. importadas desde RR.HH.), principal primero.
// El paso 3 las precarga como unidades adicionales para no perderlas en silencio al vincular.
exports.OnboardingCandidateSchema = OnboardingCandidateBaseSchema.extend({
    unidadesActivas: zod_1.z
        .array(zod_1.z.object({ idUnidad: zod_1.z.number().int().positive(), esPrincipal: zod_1.z.boolean() }))
        .optional()
        .default([]),
});
exports.OnboardingCandidateEvidenceSchema = OnboardingCandidateBaseSchema.extend({
    rut: zod_1.z.string().nullable(),
    correo: zod_1.z.string().nullable(),
    idUsuario: zod_1.z.number().int().positive().nullable(),
    estamento: zod_1.z.object({ slug: zod_1.z.string().nullable(), nombre: zod_1.z.string().nullable() }).nullable(),
});
exports.OnboardingHelpContextSchema = zod_1.z.object({
    reason: exports.OnboardingHelpReasonSchema,
    blockCode: exports.OnboardingBlockCodeSchema.optional(),
    message: zod_1.z.string(),
    candidateCount: zod_1.z.number().int().nonnegative(),
});
exports.OnboardingFormerStudentMigrationSchema = zod_1.z.object({
    kind: zod_1.z.literal('former_student_academic_email'),
    from: zod_1.z.string().nullable(),
    to: zod_1.z.string(),
});
const OnboardingContinueResponseSchema = zod_1.z.object({
    status: zod_1.z.literal('continue'),
    candidate: exports.OnboardingCandidateSchema,
    migration: exports.OnboardingFormerStudentMigrationSchema.optional(),
});
const OnboardingNoMatchResponseSchema = zod_1.z.union([
    zod_1.z.object({
        status: zod_1.z.literal('no_match'),
        canCreateNew: zod_1.z.literal(true),
        help: exports.OnboardingHelpContextSchema.optional(),
    }),
    zod_1.z.object({
        status: zod_1.z.literal('no_match'),
        canCreateNew: zod_1.z.literal(false),
        help: exports.OnboardingHelpContextSchema,
    }),
]);
const OnboardingBlockedResponseSchema = zod_1.z.object({
    status: zod_1.z.literal('blocked'),
    blockCode: exports.OnboardingBlockCodeSchema,
    help: exports.OnboardingHelpContextSchema,
});
exports.OnboardingMatchResponseSchema = zod_1.z.union([
    OnboardingContinueResponseSchema,
    OnboardingNoMatchResponseSchema,
    OnboardingBlockedResponseSchema,
]);
const OnboardingIdentityHelpReasonSchema = zod_1.z.union([
    exports.OnboardingBlockCodeSchema,
    zod_1.z.enum(['no_match', 'not_me']),
]);
exports.OnboardingHelpRequestSchema = zod_1.z.union([
    zod_1.z.object({
        reason: zod_1.z.literal('manual_help'),
        rut: zod_1.z.string().optional(),
        orcid: zod_1.z.string().optional(),
        message: zod_1.z.string().trim().min(1, 'El mensaje es obligatorio').max(2000, 'El mensaje no puede superar 2000 caracteres'),
    }),
    zod_1.z.object({
        rut: zod_1.z.string({ required_error: 'El RUT es obligatorio' }).min(1, 'El RUT es obligatorio'),
        orcid: zod_1.z.string({ required_error: 'El ORCID es obligatorio' }).min(1, 'El ORCID es obligatorio'),
        reason: OnboardingIdentityHelpReasonSchema,
        message: zod_1.z.string().trim().max(2000, 'El mensaje no puede superar 2000 caracteres').optional(),
    }),
]);
exports.OnboardingManualFallbackSchema = zod_1.z.object({
    to: zod_1.z.string().email(),
    subject: zod_1.z.string(),
    body: zod_1.z.string(),
    mailto: zod_1.z.string(),
});
exports.OnboardingHelpDeliveryStatusSchema = zod_1.z.enum([
    'automated',
    'partial',
    'manual_fallback_required',
]);
exports.OnboardingHelpResponseSchema = zod_1.z.object({
    ok: zod_1.z.literal(true),
    reference: zod_1.z.string(),
    alertCreated: zod_1.z.boolean(),
    emailSent: zod_1.z.boolean(),
    deliveryStatus: exports.OnboardingHelpDeliveryStatusSchema,
    manualFallback: exports.OnboardingManualFallbackSchema,
});
// ─── Onboarding — Paso 2 + completar registro ────────────────
const OnboardingCompleteFieldsSchema = zod_1.z.object({
    rut: zod_1.z.string().min(1, 'El RUT es obligatorio'),
    orcid: zod_1.z.string().min(1, 'El ORCID es obligatorio'),
    // Opcionales (paso 4): id o link al perfil WoS/Scopus. Se guardan crudos y el
    // backend extrae el id canónico hacia investigador.wos_researcher_id /
    // scopus_author_id (y genera el enlace externo). Cadena vacía = no informado.
    wosResearcherId: zod_1.z.string().max(1024).optional().nullable(),
    scopusAuthorId: zod_1.z.string().max(1024).optional().nullable(),
    // Obligatorios.
    nombre1: zod_1.z.string().min(1, 'El primer nombre es obligatorio'),
    nombre2: zod_1.z.string().optional().nullable(),
    apellido1: zod_1.z.string().min(1, 'El primer apellido es obligatorio'),
    apellido2: zod_1.z.string().optional().nullable(),
    formatoNombre: zod_1.z
        .enum(['primer_nombre', 'segundo_nombre', 'nombre_compuesto', 'nombre_invertido'])
        .optional()
        .nullable(),
    idUnidad: zod_1.z.number({ required_error: 'La unidad es obligatoria' }).int().positive('La unidad es obligatoria'),
    // Nombre libre para la unidad "Otra": si el investigador eligió "Otra", puede
    // declarar cuál es su unidad real. Se usa solo cuando idUnidad === UNIDAD_OTRA_ID
    // y se persiste como el valorTexto del dato-espejo 'unidad'. Vacío/omitido → "Otra".
    nombreUnidadOtra: zod_1.z.string().trim().max(120).optional().nullable(),
    // R65: unidades adicionales elegidas directamente en el paso 3 (sin solicitud). Las reglas
    // (no repetir la principal ni otra adicional, tope y una sola "Otra") viven en
    // `validarEstadoAdscripcion`; se aplican en el `superRefine` de `OnboardingCompleteSchema`
    // porque un `discriminatedUnion` solo admite objetos planos. `nombreUnidadOtra` aplica a la
    // ranura (principal o adicional) que tenga "Otra".
    idUnidadesAdicionales: zod_1.z.array(zod_1.z.number().int().positive()).optional().default([]),
    idEstamento: zod_1.z
        .number({ required_error: 'El rol de investigador es obligatorio' })
        .int()
        .positive('El rol de investigador es obligatorio'),
    // Sexo declarado en el paso 1: se persiste en investigador.genero y se copia a
    // investigador_dato_personal (codigo='genero'), igual que el correo de cuenta.
    sexo: zod_1.z.enum(['masculino', 'femenino'], { required_error: 'El sexo es obligatorio' }),
    // Visibilidad del sexo y del correo de cuenta en el perfil (privados por defecto).
    generoPublico: zod_1.z.boolean().optional().default(false),
    correoPublico: zod_1.z.boolean().optional().default(false),
    lineas: zod_1.z.array(zod_1.z.number().int().positive()).optional().default([]),
});
const OnboardingCompleteIntentSchema = zod_1.z.discriminatedUnion('intent', [
    OnboardingCompleteFieldsSchema.extend({
        intent: zod_1.z.literal('link_existing'),
        idInvestigador: zod_1.z.number().int().positive(),
    }),
    OnboardingCompleteFieldsSchema.extend({
        intent: zod_1.z.literal('create_new'),
        idInvestigador: zod_1.z.never().optional(),
    }),
]);
const OnboardingCompleteLegacyLinkSchema = OnboardingCompleteFieldsSchema.extend({
    intent: zod_1.z.undefined().optional(),
    idInvestigador: zod_1.z.number().int().positive(),
}).transform(({ intent: _intent, ...data }) => ({
    ...data,
    intent: 'link_existing',
}));
exports.OnboardingCompleteSchema = zod_1.z
    .union([OnboardingCompleteIntentSchema, OnboardingCompleteLegacyLinkSchema])
    .superRefine((data, ctx) => {
    const violaciones = (0, adscripcion_js_1.validarEstadoAdscripcion)({
        idUnidadPrincipal: data.idUnidad,
        idUnidadesAdicionales: data.idUnidadesAdicionales,
    });
    for (const violacion of violaciones) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['idUnidadesAdicionales'],
            message: (0, adscripcion_js_1.mensajeViolacionAdscripcion)(violacion),
        });
    }
});
// ─── Onboarding — líneas por unidad seleccionada ──────────────
// R65: la consulta acepta `idUnidades` (lista separada por comas, 1 a 4: la principal + hasta 3
// adicionales) y conserva `idUnidad` (un solo id) para los llamadores anteriores. Siempre se
// normaliza a `idUnidades`, sin repetidos y respetando el orden (la principal primero).
const MAX_UNIDADES_CONSULTA_LINEAS = 1 + adscripcion_js_1.MAX_UNIDADES_ADICIONALES;
const IdUnidadQuerySchema = zod_1.z.coerce.number().int().positive('La unidad es obligatoria');
exports.OnboardingLineasQuerySchema = zod_1.z
    .object({
    idUnidades: zod_1.z.string().optional(),
    idUnidad: IdUnidadQuerySchema.optional(),
})
    .transform((query, ctx) => {
    const partes = query.idUnidades !== undefined ? query.idUnidades.split(',') : [];
    const ids = [];
    for (const parte of partes) {
        const id = IdUnidadQuerySchema.safeParse(parte.trim() === '' ? undefined : parte);
        if (!id.success) {
            ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['idUnidades'], message: 'La unidad es obligatoria' });
            return zod_1.z.NEVER;
        }
        if (!ids.includes(id.data))
            ids.push(id.data);
    }
    if (query.idUnidades === undefined && query.idUnidad !== undefined)
        ids.push(query.idUnidad);
    if (ids.length === 0) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['idUnidades'], message: 'La unidad es obligatoria' });
        return zod_1.z.NEVER;
    }
    if (ids.length > MAX_UNIDADES_CONSULTA_LINEAS) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['idUnidades'],
            message: `Se permiten como máximo ${MAX_UNIDADES_CONSULTA_LINEAS} unidades.`,
        });
        return zod_1.z.NEVER;
    }
    return { idUnidades: ids };
});
// Cada opción dice por qué unidad(es) elegida(s) se ofrece (`viaUnidades`, en el orden de la
// consulta): la web agrupa la tabla con eso. Default [] para tolerar respuestas anteriores.
exports.OnboardingLineaOptionSchema = investigador_schema_js_1.InvestigadorLineaOptionSchema.extend({
    viaUnidades: zod_1.z.array(zod_1.z.number()).optional().default([]),
});
exports.OnboardingLineasResponseSchema = zod_1.z.object({
    lineas: zod_1.z.array(exports.OnboardingLineaOptionSchema),
});
